import type { StateCreator } from 'zustand';
import type { CustomEntry } from '@my-fullstack-app/itinerary-types';
import { addMinutes, applyPatch, generateId, sortByStartTime } from '../helpers';
import type { ItineraryState } from '../itinerary-store';
import { markTripDirty } from '../sync-bridge';
import { track } from '../../lib/analytics';
import type { TripField } from '../../lib/sync-manager';
import { buildConversion, type ConversionTarget } from '../../lib/placeholder-conversion';
import { nextStartTimeFromAllItems, removeDayItem } from './day-item-helpers';

export interface CustomSlice {
  /**
   * Create a placeholder on a day and return its id. The id lets the
   * caller (e.g. the add-menu speed dial) navigate straight to the new
   * placeholder's detail screen for editing.
   */
  addCustom: (
    tripId: string,
    dayId: string,
    params: { title: string; subtitle?: string | undefined; notes?: string | undefined; startTime?: string | undefined; endTime?: string | undefined },
    targetStartTime?: string | undefined
  ) => string;
  removeCustom: (tripId: string, dayId: string, customId: string) => void;
  updateCustom: (
    tripId: string,
    dayId: string,
    customId: string,
    params: {
      title?: string | undefined;
      // null clears the field (see applyPatch); optional text fields
      // the user can empty back out.
      subtitle?: string | null | undefined;
      notes?: string | null | undefined;
      startTime?: string | undefined;
      endTime?: string | undefined;
    }
  ) => void;
  /**
   * Convert a placeholder into a Stay, Journey, or Place ('visit').
   * Builds the target entity from the placeholder's title / time /
   * costs, migrates its note + tag, removes the placeholder, and
   * returns the new entity's id (or `undefined` when the placeholder
   * can't be found). The whole conversion is a single undoable step.
   */
  convertCustom: (
    tripId: string,
    dayId: string,
    customId: string,
    target: ConversionTarget
  ) => string | undefined;
}

export const createCustomSlice: StateCreator<ItineraryState, [], [], CustomSlice> = (set, get) => ({
  addCustom: (tripId, dayId, { title, subtitle, notes, startTime: paramStartTime, endTime: paramEndTime }, targetStartTime) => {
    get()._capture();
    const id = generateId();
    set((s) => {
      const trip = s.trips.find((t) => t.id === tripId);
      const day = trip?.days.find((d) => d.id === dayId);
      const startTime = paramStartTime ?? targetStartTime ?? nextStartTimeFromAllItems(day);
      const custom: CustomEntry = {
        id,
        title,
        subtitle,
        notes,
        startTime,
        endTime: paramEndTime ?? addMinutes(startTime, 60),
      };
      return {
        trips: s.trips.map((t) => {
          if (t.id !== tripId) return t;
          return {
            ...t,
            days: t.days.map((d) => {
              if (d.id !== dayId) return d;
              return { ...d, customs: sortByStartTime([...(d.customs ?? []), custom]) };
            }),
          };
        }),
      };
    });
    markTripDirty(tripId, ['days']);
    track('custom.added', {
      tripId,
      dayId,
      hasSubtitle: !!subtitle,
      hasNotes: !!notes,
    });
    return id;
  },

  removeCustom: (tripId, dayId, customId) => {
    removeDayItem(set, get, 'customs', tripId, dayId, customId);
  },

  updateCustom: (tripId, dayId, customId, params) => {
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          days: t.days.map((d) => {
            if (d.id !== dayId) return d;
            const updated = (d.customs ?? []).map((c) =>
              c.id !== customId ? c : applyPatch({ ...c }, params),
            );
            if (params.startTime != null) sortByStartTime(updated);
            return { ...d, customs: updated };
          }),
        };
      }),
    }));
    markTripDirty(tripId, ['days']);
  },

  convertCustom: (tripId, dayId, customId, target) => {
    const s0 = get();
    const trip = s0.trips.find((t) => t.id === tripId);
    const day = trip?.days.find((d) => d.id === dayId);
    const custom = day?.customs?.find((c) => c.id === customId);
    if (!trip || !day || !custom) return undefined;

    get()._capture();
    const result = buildConversion(custom, target, day.date, s0.notesMap[customId]);
    const newId = result.entity.id;
    const tag = s0.tagsMap[customId];

    set((s) => {
      const trips = s.trips.map((t) => {
        if (t.id !== tripId) return t;
        // Drop the placeholder from its day.
        const days = t.days.map((d) =>
          d.id === dayId
            ? { ...d, customs: (d.customs ?? []).filter((c) => c.id !== customId) }
            : d,
        );
        if (result.target === 'stay') {
          return { ...t, days, stays: [...(t.stays ?? []), result.entity] };
        }
        if (result.target === 'journey') {
          return { ...t, days, journeys: [...(t.journeys ?? []), result.entity] };
        }
        // visit → Place lands on the placeholder's own day, time-sorted.
        return {
          ...t,
          days: days.map((d) =>
            d.id === dayId
              ? { ...d, places: sortByStartTime([...d.places, result.entity]) }
              : d,
          ),
        };
      });

      // Migrate note + tag from the placeholder id onto the new entity.
      const notesMap = { ...s.notesMap };
      const notesMetaMap = { ...s.notesMetaMap };
      delete notesMap[customId];
      delete notesMetaMap[customId];
      if (result.note) {
        notesMap[newId] = result.note;
        notesMetaMap[newId] = { updatedAt: new Date().toISOString() };
      }
      const tagsMap = { ...s.tagsMap };
      delete tagsMap[customId];
      if (tag) tagsMap[newId] = tag;

      return { trips, notesMap, notesMetaMap, tagsMap };
    });

    // 'days' always changes (placeholder removed; a Visit also lands on a
    // day). Trip-scoped targets touch their own wire field. 'notes' /
    // 'tags' cover the migrated note + tag.
    const fields: TripField[] =
      result.target === 'stay'
        ? ['days', 'stays', 'notes', 'tags']
        : result.target === 'journey'
          ? ['days', 'journeys', 'notes', 'tags']
          : ['days', 'notes', 'tags'];
    markTripDirty(tripId, fields);

    track('custom.converted', { tripId, dayId, target });
    return newId;
  },
});
